/**
 * @file The live consultation chat, as both sides see it. Presentational only.
 * @module components/consult/ConsultChatPanel
 *
 * S4 of ClickUp 86d42x0pt. It holds no socket, no room id and no upload client — `useConsultChat`
 * owns all three — so the practitioner surface and the unauthenticated patient page mount the same
 * component with different callbacks.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Tooltip } from "antd";
import { FileImageOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, PaperClipOutlined, SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { ConsultChatAttachment, ConsultChatMessage } from "../../types/appointments/ConsultChatTypes";
import { formatFileSize } from "../../utils/fileUtils";
import "./ConsultChatPanel.css";

/** talk's own cap — the composer hard-stops here rather than letting the server refuse the message. */
export const CHAT_CONTENT_MAX_LENGTH = 4000;

/** Mirrors healthcare's `FILE_RULES` mimes, so the picker offers only what the server will accept. */
const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.jpg,.jpeg,.png";

const ROLE_LABELS: Record<ConsultChatMessage["senderRole"], string> = {
	practitioner: "Practitioner",
	patient: "Patient",
};

/** A patient's `senderName` is `null` by design — talk refuses to take it from their payload. */
const senderDisplayName = (message: ConsultChatMessage): string => message.senderName?.trim() || ROLE_LABELS[message.senderRole];

const attachmentIcon = (mime: string) => {
	if (mime.startsWith("image/")) return <FileImageOutlined className="consult-chat-attachment-icon" />;
	if (mime === "application/pdf") return <FilePdfOutlined className="consult-chat-attachment-icon" />;
	if (mime.includes("word") || mime.includes("officedocument.wordprocessing")) return <FileWordOutlined className="consult-chat-attachment-icon" />;
	return <FileOutlined className="consult-chat-attachment-icon" />;
};

export interface ConsultChatPanelProps {
	messages: ConsultChatMessage[];
	currentRole: ConsultChatMessage["senderRole"];
	/** `false` means the send was refused — the draft is kept so nothing the user typed is lost. */
	onSend: (content: string) => boolean | void;
	onAttach?: (file: File) => Promise<boolean | void> | boolean | void;
	disabled?: boolean;
	/** Differs per side — the practitioner and the patient are told different things about silence. */
	emptyText?: string;
	error?: string | null;
	onDismissError?: () => void;
}

const AttachmentRow: React.FC<{ attachment: ConsultChatAttachment }> = ({ attachment }) => (
	<div className="consult-chat-attachment" data-testid="consult-chat-attachment">
		{attachmentIcon(attachment.mime)}
		<div className="consult-chat-attachment-info">
			<span className="consult-chat-attachment-name">{attachment.name}</span>
			<span className="consult-chat-attachment-size">{formatFileSize(attachment.size)}</span>
		</div>
	</div>
);

const ConsultChatPanel: React.FC<ConsultChatPanelProps> = ({ messages, currentRole, onSend, onAttach, disabled = false, emptyText, error, onDismissError }) => {
	const [draft, setDraft] = useState("");
	const [attaching, setAttaching] = useState(false);
	const [localError, setLocalError] = useState<string | null>(null);

	const messagesRef = useRef<HTMLDivElement | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		const container = messagesRef.current;
		if (container) container.scrollTop = container.scrollHeight;
	}, [messages.length]);

	const handleSend = useCallback(() => {
		if (disabled) return;

		const trimmed = draft.trim();
		if (!trimmed) return;

		setLocalError(null);
		if (onSend(trimmed) !== false) setDraft("");
	}, [disabled, draft, onSend]);

	const handleFileChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			// Cleared immediately so picking the same file twice still fires a change event.
			event.target.value = "";
			if (!file || !onAttach) return;

			setLocalError(null);
			setAttaching(true);
			try {
				await onAttach(file);
			} catch {
				setLocalError("The file could not be uploaded. Please try again.");
			} finally {
				setAttaching(false);
			}
		},
		[onAttach],
	);

	const shownError = error ?? localError;
	const atLimit = draft.length >= CHAT_CONTENT_MAX_LENGTH;

	return (
		<div className="consult-chat-panel" data-testid="consult-chat-panel">
			<div className="consult-chat-messages" ref={messagesRef} data-testid="consult-chat-messages">
				{messages.length === 0 ? (
					<div className="consult-chat-empty" data-testid="consult-chat-empty">
						{emptyText || "No messages yet."}
					</div>
				) : (
					messages.map((message) => (
						<div
							key={message.id}
							className={`consult-chat-row${message.senderRole === currentRole ? " consult-chat-row-own" : ""}`}
							data-testid="consult-chat-message"
							data-role={message.senderRole}
						>
							<div className="consult-chat-bubble">
								<div className="consult-chat-meta">
									<span className="consult-chat-sender">{senderDisplayName(message)}</span>
									<span className="consult-chat-time">{message.timestamp ? dayjs(message.timestamp).format("HH:mm") : ""}</span>
								</div>

								{/* Rendered as TEXT — user-authored content from a party who may be unauthenticated. */}
								{message.content ? <div className="consult-chat-text">{message.content}</div> : null}

								{message.attachments.map((attachment, index) => (
									<AttachmentRow key={attachment.id || `${message.id}-${index}`} attachment={attachment} />
								))}

								{message.attachments.length > 0 ? (
									<span className="consult-chat-attachment-hint">Saved to this patient's files — open the Files tab to view it.</span>
								) : null}
							</div>
						</div>
					))
				)}
			</div>

			{shownError ? (
				<div className="consult-chat-error">
					<Alert
						type="error"
						showIcon
						banner
						message={shownError}
						closable
						onClose={() => {
							setLocalError(null);
							onDismissError?.();
						}}
						data-testid="consult-chat-error"
					/>
				</div>
			) : null}

			<div className="consult-chat-composer">
				<div className="consult-chat-composer-row">
					{onAttach ? (
						<>
							<input
								type="file"
								ref={fileInputRef}
								accept={ACCEPTED_FILE_TYPES}
								onChange={handleFileChange}
								style={{ display: "none" }}
								data-testid="consult-chat-file-input"
							/>
							<Tooltip title="Attach a file">
								<Button
									icon={<PaperClipOutlined />}
									onClick={() => fileInputRef.current?.click()}
									disabled={disabled || attaching}
									loading={attaching}
									data-testid="consult-chat-attach"
									aria-label="Attach a file"
								/>
							</Tooltip>
						</>
					) : null}

					<Input.TextArea
						className="consult-chat-input"
						value={draft}
						onChange={(event) => setDraft(event.target.value)}
						onKeyDown={(event) => {
							if (event.key !== "Enter" || event.shiftKey) return;
							event.preventDefault();
							handleSend();
						}}
						placeholder="Type a message"
						autoSize={{ minRows: 1, maxRows: 4 }}
						maxLength={CHAT_CONTENT_MAX_LENGTH}
						disabled={disabled}
						data-testid="consult-chat-input"
					/>

					<Button
						type="primary"
						icon={<SendOutlined />}
						onClick={handleSend}
						disabled={disabled || draft.trim().length === 0}
						data-testid="consult-chat-send"
						aria-label="Send message"
					/>
				</div>

				<span className={`consult-chat-counter${atLimit ? " consult-chat-counter-limit" : ""}`} data-testid="consult-chat-counter">
					{draft.length}/{CHAT_CONTENT_MAX_LENGTH}
				</span>
			</div>
		</div>
	);
};

export default ConsultChatPanel;
